export const DEFAULT_CANCELLATION_POLICY_HOURS = 6;
export const DEFAULT_LATE_GRACE_MINUTES = 10;

type Unit = "hour" | "day";

const pluralize = (value: number, unit: Unit) => {
  const absolute = Math.abs(value);
  const unitLabel = unit + (absolute === 1 ? "" : "s");
  return `${value} ${unitLabel}`;
};

const hoursToLabel = (hours: number) => {
  if (hours <= 0) {
    return {
      advanceText: "at any time before your appointment begins",
      windowText: "after your scheduled start time",
    };
  }

  if (hours % 24 === 0) {
    const days = hours / 24;
    const dayLabel = pluralize(days, "day");
    return {
      advanceText: `at least ${dayLabel} in advance`,
      windowText: `within ${dayLabel} of your appointment`,
    };
  }

  const hourLabel = pluralize(hours, "hour");
  return {
    advanceText: `at least ${hourLabel} in advance`,
    windowText: `within ${hourLabel} of your appointment`,
  };
};

export const formatCancellationPolicy = (hours?: number | null) => {
  const safeHours =
    Number.isFinite(hours) && typeof hours === "number"
      ? Math.max(0, Math.trunc(hours))
      : DEFAULT_CANCELLATION_POLICY_HOURS;

  const { advanceText, windowText } = hoursToLabel(safeHours);

  return {
    hours: safeHours,
    advanceText,
    windowText,
  };
};

/**
 * Returns the default cancellation policy message shown to customers
 * when the business has not set a custom policy note.
 */
export const getDefaultCancellationPolicyMessage = (
  hours?: number | null,
  lateGraceMinutes?: number | null,
): string => {
  const { advanceText } = formatCancellationPolicy(hours);
  const grace =
    typeof lateGraceMinutes === "number" && Number.isFinite(lateGraceMinutes)
      ? Math.max(0, Math.trunc(lateGraceMinutes))
      : DEFAULT_LATE_GRACE_MINUTES;
  return `If you are more than ${grace} minutes late and it affects the next booking, your appointment may be canceled.\nCancellations or rescheduling must be made ${advanceText}.`;
};

/**
 * Returns the custom policy note if set, otherwise the default message.
 */
export const getCancellationPolicyText = (
  hours?: number | null,
  note?: string | null,
  lateGraceMinutes?: number | null,
): string => {
  const trimmed = note?.trim();
  if (trimmed) return trimmed;
  return getDefaultCancellationPolicyMessage(hours, lateGraceMinutes);
};

/**
 * Whether canceling at `now` falls inside the business's cancellation window:
 * the API's breach rule, shared so customer copy predicts what the cancel
 * decides. A disabled window is never breached (booking-flow § 9).
 */
export const isInsideCancellationWindow = (input: {
  policyEnabled: boolean | null | undefined;
  policyHours: number | null | undefined;
  startsAt: Date | string;
  now?: Date;
}): boolean => {
  if (input.policyEnabled === false) return false;
  const hours = input.policyHours ?? DEFAULT_CANCELLATION_POLICY_HOURS;
  const startsAt =
    input.startsAt instanceof Date ? input.startsAt : new Date(input.startsAt);
  const now = input.now ?? new Date();
  return (startsAt.getTime() - now.getTime()) / 3_600_000 < hours;
};

/*
 * Customer copy for the cancellation deposit rule (refund gap 1): a customer
 * who cancels before the window gets the upfront payment back automatically;
 * inside the window, or on a no-show, the business keeps it. A disabled
 * window always refunds a cancel.
 */

/** "deposit" when part of the total was charged, "payment" when all of it. */
export type UpfrontPaymentNoun = "deposit" | "payment";

/**
 * booking.payment.refundPolicy / booking.payment.refundPolicyOff: shown where
 * an upfront charge is taken. A business's custom policy note never replaces
 * it; the note is shown separately.
 */
export const getUpfrontRefundPolicyText = (input: {
  policyEnabled: boolean | null | undefined;
  policyHours: number | null | undefined;
  noun: UpfrontPaymentNoun;
}): string => {
  if (input.policyEnabled === false) {
    return `If you cancel, your ${input.noun} is refunded automatically.`;
  }
  const { advanceText } = formatCancellationPolicy(input.policyHours);
  return `Cancel ${advanceText} and your ${input.noun} is refunded automatically. Later cancellations and no-shows keep the ${input.noun}.`;
};

/**
 * booking.cancel.upfrontRefund / booking.cancel.upfrontKept: before a
 * customer confirms a cancel on the manage page. That page must not reveal
 * whether anything was paid before the cancel OTP (`BF-DRIFT-001`), so the
 * copy is conditional and states no amount.
 */
export const getCancelUpfrontNotice = (input: {
  insideWindow: boolean;
  policyHours: number | null | undefined;
}): string => {
  if (!input.insideWindow) {
    return "If you paid in advance, it will be refunded to your card.";
  }
  const { windowText } = formatCancellationPolicy(input.policyHours);
  return `This is ${windowText}, so anything you paid in advance won't be refunded.`;
};

/**
 * The same notice when the upfront charge is known (voice, after the caller
 * was matched to the appointment). No amount is read before the cancel.
 */
export const getCancelDepositNotice = (input: {
  insideWindow: boolean;
  policyHours: number | null | undefined;
  noun: UpfrontPaymentNoun;
}): string => {
  if (!input.insideWindow) {
    return `Your ${input.noun} will be refunded to your card.`;
  }
  const { windowText } = formatCancellationPolicy(input.policyHours);
  return `This is ${windowText}, so your ${input.noun} won't be refunded.`;
};

/**
 * Whether any booked service's catalog policy takes money up front. A catalog
 * fact (the public booking page shows it), never the booking's payment state
 * or a private customer policy, so a bare-ID page may use it.
 */
export const servicesTakeUpfrontPayment = (
  services: ReadonlyArray<{
    paymentRequirement?: string | null;
    hasPayAdvance?: boolean | null;
  }>,
): boolean =>
  services.some(
    (service) =>
      service.paymentRequirement === "DEPOSIT" ||
      service.paymentRequirement === "FULL" ||
      service.hasPayAdvance === true,
  );

/**
 * booking.confirmation.refundPolicy / refundPolicyOff: the refund rule on a
 * confirmation or review page. Conditional and amount-free, because the page
 * may be reached by an appointment ID alone (booking-flow § 10).
 */
export const getConfirmationRefundPolicyText = (input: {
  policyEnabled: boolean | null | undefined;
  policyHours: number | null | undefined;
}): string => {
  if (input.policyEnabled === false) {
    return "If you paid in advance and cancel, it is refunded automatically.";
  }
  const { advanceText } = formatCancellationPolicy(input.policyHours);
  return `If you paid in advance, cancel ${advanceText} and it is refunded automatically. Later cancellations and no-shows keep it.`;
};

const formatMinorUsd = (minor: number) => `$${(minor / 100).toFixed(2)}`;

/**
 * booking.cancel.refundStarted / booking.cancel.upfrontKeptResult /
 * booking.cancel.refundSeparately: after a customer cancel, from the API's
 * `deposit` outcome. Null when nothing was charged (or the API is older).
 */
export const getCancelledDepositResultText = (
  deposit: { outcome: string; amountMinor?: number } | null | undefined,
): string | null => {
  const amount = formatMinorUsd(deposit?.amountMinor ?? 0);
  switch (deposit?.outcome) {
    case "REFUND_STARTED":
      return `Your ${amount} refund has started. It can take 5–10 business days to reach your card.`;
    case "KEPT":
      return `Under the cancellation policy, the ${amount} you paid in advance isn't refunded.`;
    case "NEEDS_ATTENTION":
      return "The business will handle your refund separately.";
    default:
      return null;
  }
};

export interface CancellationLeadTime {
  /**
   * Whole minutes between the cancellation and the appointment start.
   * Positive = canceled before the start, negative = after it.
   */
  minutesBeforeStart: number;
  /** Magnitude only, two units at most: `8d 21h`, `13h 4m`, `20m`, `0m`. */
  durationLabel: string;
  /** `8d 21h before start`, `20m after start`, `at start time`. */
  label: string;
  isAfterStart: boolean;
}

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/**
 * Two units at most, largest first: `8d 21h`, `13h 4m`, `20m`. Precision drops
 * as the span grows because nobody converts `213h 35m` in their head, and the
 * minutes of a cancellation eight days out carry no information — the policy
 * window that makes minutes matter is measured in single-digit hours.
 */
const formatDuration = (totalMinutes: number): string => {
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = totalMinutes % MINUTES_PER_HOUR;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

const toDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * How far ahead of the appointment a cancellation landed — the one fact that
 * turns "Canceled" into something a stylist can act on. Two same-day
 * cancellations are not the same event if one came a week ahead and the other
 * twenty minutes after the client was due in the chair.
 *
 * Shared because three surfaces show it: the dashboard cancellations feed and
 * the client-history drawer in both the dashboard and the staff portal. Same
 * phrasing everywhere, so "20m after start" means the same thing on each.
 */
export const describeCancellationLeadTime = (
  canceledAt: string | Date | null | undefined,
  startsAt: string | Date | null | undefined,
): CancellationLeadTime | null => {
  const canceled = toDate(canceledAt);
  const start = toDate(startsAt);
  if (!canceled || !start) return null;

  // Truncated, not rounded: 59 seconds before the start is "0m before", never
  // "1m before" — the label should never claim more lead time than there was.
  const minutesBeforeStart = Math.trunc(
    (start.getTime() - canceled.getTime()) / 60000,
  );
  const durationLabel = formatDuration(Math.abs(minutesBeforeStart));

  const isAfterStart = minutesBeforeStart < 0;
  const label =
    minutesBeforeStart === 0
      ? "at start time"
      : `${durationLabel} ${isAfterStart ? "after" : "before"} start`;

  return { minutesBeforeStart, durationLabel, label, isAfterStart };
};
